import { denormalizeTransactions } from './denormalizeTransactions';

const NEEDS_KW   = ['mortgage','utilities','hydro','electricity','internet','phone','telecom','insurance','property tax','vehicle lease','child care','childcare','daycare','grocery','groceries','gas','pharmacy','personal care','healthcare','medical','dental','fitness','home security','transit','transport','auto','car'];
const SAVINGS_KW = ['savings','rrsp','tfsa','investment','resp','retirement'];
const DEBT_KW    = ['interest charge','student loan','line of credit','loan payment','charity','donation','giving','tithe'];

function bucket(t) {
  const text = ((t.category || '') + ' ' + (t.description || '')).toLowerCase();
  if (NEEDS_KW.some(k => text.includes(k)))   return 'needs';
  if (SAVINGS_KW.some(k => text.includes(k))) return 'savings';
  if (DEBT_KW.some(k => text.includes(k)))    return 'debt';
  return 'discretionary';
}

function isCCPayment(t) {
  const text = ((t.category || '') + ' ' + (t.description || '')).toLowerCase();
  return text.includes('credit card') || text.includes('visa payment') || text.includes('mastercard payment');
}

/**
 * Computes the 70/20/10 budget rule breakdown from a customer profile.
 * Returns averages, percentages vs. targets, monthly trend data, and insights.
 *
 * @param {object} profile - profile object with an `accounts` property
 * @returns {object|null}
 */
export function computeBudgetRule(profile) {
  if (!profile?.accounts) return null;

  const chqTxns = denormalizeTransactions(profile.accounts.chequing?.transactions);
  const ccTxns  = denormalizeTransactions(profile.accounts.credit_card?.transactions);

  const allDates  = [...chqTxns, ...ccTxns].map(t => t.date?.slice(0, 7)).filter(Boolean);
  const ALL_MONTHS = [...new Set(allDates)].sort();
  const numMonths  = ALL_MONTHS.length || 1;
  const MONTH_LABELS = ALL_MONTHS.map(k =>
    ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][parseInt(k.split('-')[1], 10) - 1]
  );

  // After-tax income: positive chequing transactions
  const incomeTxns  = chqTxns.filter(t => t.amount > 0);
  const totalIncome = incomeTxns.reduce((s, t) => s + t.amount, 0);
  const avgIncome   = Math.round(totalIncome / numMonths);

  // Exclude CC bill payments from chequing to avoid double-counting
  const chqDebits   = chqTxns.filter(t => t.amount < 0 && !isCCPayment(t));
  const ccPurchases = ccTxns.filter(t => t.amount < 0);

  const totals = { needs: 0, savings: 0, debt: 0, discretionary: 0 };
  [...chqDebits, ...ccPurchases].forEach(t => { totals[bucket(t)] += Math.abs(t.amount); });

  const avgNeeds = Math.round(totals.needs / numMonths);
  const avgSavings = Math.round(totals.savings / numMonths);
  const avgDebt    = Math.round(totals.debt / numMonths);
  const avgDisc    = Math.round(totals.discretionary / numMonths);
  const pctOf = (v) => avgIncome > 0 ? Math.round((v / avgIncome) * 100) : 0;

  // Monthly stacked breakdown
  const monthlyData = ALL_MONTHS.map((key, i) => {
    const mo    = { month: MONTH_LABELS[i] };
    mo.income   = Math.round(incomeTxns.filter(t => t.date?.startsWith(key)).reduce((s, t) => s + t.amount, 0));
    const txns  = [
      ...chqDebits.filter(t => t.date?.startsWith(key)),
      ...ccPurchases.filter(t => t.date?.startsWith(key)),
    ];
    const b = { needs: 0, savings: 0, debt: 0, discretionary: 0 };
    txns.forEach(t => { b[bucket(t)] += Math.abs(t.amount); });
    mo.Needs            = Math.round(b.needs);
    mo.Savings          = Math.round(b.savings);
    mo['Debt & Giving'] = Math.round(b.debt);
    mo.Discretionary    = Math.round(b.discretionary);
    return mo;
  });

  const pctNeeds   = pctOf(avgNeeds);
  const pctSavings = pctOf(avgSavings);
  const pctDebt    = pctOf(avgDebt);
  const pctDisc    = pctOf(avgDisc);

  // Insights
  const insights = [];
  if (pctNeeds > 75) {
    insights.push({ icon: '⚠️', text: `Needs spending (${pctNeeds}%) is ${pctNeeds - 70}pts above the 70% target. Housing, transport, and childcare are likely drivers.` });
  } else {
    insights.push({ icon: '✅', text: `Needs spending is ${pctNeeds}% — within the 70% guideline. Essential costs look manageable.` });
  }
  if (pctSavings >= 20) {
    insights.push({ icon: '✅', text: `Savings rate of ${pctSavings}% meets or exceeds the 20% target. Great discipline!` });
  } else {
    const gap = Math.round((0.20 - pctSavings / 100) * avgIncome);
    insights.push({ icon: '💡', text: `Savings are at ${pctSavings}% of income. Adding $${gap.toLocaleString()}/mo would hit the 20% target.` });
  }
  if (pctDisc > 20) {
    insights.push({ icon: '⚠️', text: `Discretionary spending is ${pctDisc}% of income ($${avgDisc.toLocaleString()}/mo). Reducing dining, subscriptions, or shopping could free up savings room.` });
  } else if (pctDisc > 0) {
    insights.push({ icon: '💡', text: `Discretionary spending is ${pctDisc}% ($${avgDisc.toLocaleString()}/mo) — your lifestyle flex spending outside the 70/20/10 rule.` });
  }

  return {
    avgIncome, avgNeeds, avgSavings, avgDebt, avgDisc,
    pctNeeds, pctSavings, pctDebt, pctDisc,
    targetNeeds:   Math.round(avgIncome * 0.70),
    targetSavings: Math.round(avgIncome * 0.20),
    targetDebt:    Math.round(avgIncome * 0.10),
    monthlyData, numMonths, insights,
  };
}
